import { useEffect, useState } from "react";
import { useUserProfile } from "@hooks";
import {
  CallListType,
  NodeType,
  NodeRefFullInput,
  useGetConsultantCallListDataLazyQuery
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { ListUserSelector } from "@containers/list-user-selector/list-user-selector";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  makeStyles
} from "@material-ui/core";
import { TextField, useSnackbarsPipeline } from "@bonliva-crm/ui";
import { useCreateCallList } from "../hooks/create-call-list";

type Props = {
  open: boolean;
  type: CallListType;
  consultantId?: string;
  onClose: () => void;
};

export function NewListModal({ open, consultantId, type, onClose }: Props) {
  const userProfile = useUserProfile();
  const classes = useStyles();
  const snack = useSnackbarsPipeline();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([userProfile.id]);
  const [nodes, setNodes] = useState<NodeRefFullInput[]>([]);
  const [name, setName] = useState<string>("");

  const [getConsultantCallListData, { loading }] = useGetConsultantCallListDataLazyQuery({
    onError(error) {
      console.error(error);
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-first"
  });

  const { createCallList } = useCreateCallList({
    onCompleted: () => {
      snack.showSuccess(locale.Saved);
      clearInputs();
      onClose();
    },
    onError: () => {
      snack.showError(
        locale.addError(
          type === CallListType.CallList
            ? locale.CallList.toLowerCase()
            : locale.ContactList.toLowerCase()
        )
      );
    }
  });

  useEffect(() => {
    if (consultantId) {
      getConsultantCallListData({ variables: { id: consultantId } })
        .then((res) =>
          setNodes([
            {
              Name: res.data.consultantById.Name,
              NodeID: res.data.consultantById._id,
              NodeType: NodeType.Consultant
            }
          ])
        )
        .catch((e) => {
          console.error(e);
          snack.showError(locale.ErrorOccurred);
        });
    }
  }, [consultantId]);

  const handleClose = () => {
    clearInputs();
    onClose();
  };

  const clearInputs = () => {
    setSelectedUserIds([]);
    setName("");
  };

  const canSubmit = name.trim().length > 0 && !loading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {type === CallListType.CallList ? locale.NewCallList : locale.NewContactList}
      </DialogTitle>

      <DialogContent>
        <div className={classes.modalContents}>
          <TextField
            label={locale.Name}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            required={true}
            autoFocus
          />

          <InputLabel>{locale.addPersons}</InputLabel>

          <ListUserSelector
            multi
            value={selectedUserIds}
            onSelect={(v) => setSelectedUserIds(v.map((x) => x._id))}
            ListSelectorProps={{
              searchAutofocus: false,
              withSearch: true
            }}
          />
        </div>
      </DialogContent>

      <DialogActions>
        <Button variant="text" color="default" onClick={handleClose}>
          {locale.Cancel}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            createCallList({
              listType: type,
              name,
              type: NodeType.Consultant,
              nodes,
              userIds: selectedUserIds
            });
          }}
          disabled={!canSubmit}
        >
          {locale.Save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  textField: {
    width: "100%",
    position: "relative",
    "&:hover": {
      cursor: "pointer"
    }
  },
  modalContents: {
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem",
    height: "100%"
  }
}));
